import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import _, { update } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useRef, useState } from 'react';
import './DetailsAccordion.messages';
import metadata from './DetailsAccordion.metadata.json5';
import messages from './DetailsAccordion.messages'
import styles from './DetailsAccordion.module.scss';
import { TranslatorContext } from '@jutro/locale';

const LOB_PAYMENTMETHOD_MAP = {
    'Homeowners': {
        'mortgagee_escrow': ['mortgagePosition_TWIA', 'FirstMortgagee'],
        /* US20716
                'alt_payor': ['type', 'ALTERNATEPAYOR_EXT'],
                'prem_fina_wellington': ['premiumFinanceCompanyType_TWIA', 'wellingtonprmfin'],
         */
        'prem_fina_other': ['premiumFinanceCompanyType_TWIA', 'other']
    },
    'CommercialProperty': {
        'mortgagee_escrow': ['type', 'MORTGAGEE'],
        /* US20716
                'alt_payor': ['type', 'ALTERNATEPAYOR_EXT'],
                'prem_fina_wellington': ['premiumFinanceCompanyType_TWIA', 'wellingtonprmfin'],
         */
        'prem_fina_other': ['premiumFinanceCompanyType_TWIA', 'other']
    }
};


function DetailsAccordion(props) {
    const { value: currentPeriodDetails, isCommercial } = props;
    const policyContactDetailsTwia = _.get(currentPeriodDetails, 'policyContactDetails_TWIA')
    const productCode = _.get(currentPeriodDetails, 'productCodeIdentifier_TWIA')
    const translator = useContext(TranslatorContext);
    const isOffer = _.get(currentPeriodDetails, 'status_TWIA') === "Offer Submitted" || _.get(currentPeriodDetails, 'status_TWIA') === "Renewing"
    const mortgageeName = useRef();

    const primaryNamedInsuredDisplayName = useCallback(() => {
        const [{ displayName = '' }] = policyPrimaryNamedInsured(policyContactDetailsTwia);

        return displayName;
    }, []);

    const policyPrimaryNamedInsured = policyContactDetails => {
        return _.filter(policyContactDetails, {
            policyContactRoles: [{
                subtype: 'PolicyPriNamedInsured',
            }]
        }) || {};
    };

    const mortgageeDisplayName = useCallback(() => {
        let contact;

        contact = policyAdditionalInterestAndContactDetailByPaymentMethod(productCode, 'mortgagee_escrow', policyContactDetailsTwia);

        const { additionalInterestContact: { displayName = undefined } = {} } = { ...contact };

        mortgageeName.current = displayName

        return displayName;
    }, []);

    const getParentKey = (productCode) => {
        if (productCode === 'Homeowners') {
            return 'additionalInterestDetails';
        } else if (productCode === 'CommercialProperty') {
            return 'cpAdditionalInterestDetails_TWIA';
        }

        return undefined;
    };

    const policyAdditionalInterestAndContactDetailByPaymentMethod = (productCode, paymentMethod, policyContactDetails) => {
        const found = policyAdditionalInterestByPaymentMethod(productCode, paymentMethod, policyContactDetails);
        const parentKey = getParentKey(productCode);

        return getAdditionalInterestContactAndDetail(parentKey, found);
    };
    const policyAdditionalInterestByPaymentMethod = (productCode, paymentMethod, policyContactDetails) => {
        const lobMap = _.get(LOB_PAYMENTMETHOD_MAP, productCode);
        const [prop, val] = _.get(lobMap, paymentMethod) || [];
        const parentKey = getParentKey(productCode);

        return policyAdditionalInterest(parentKey)([prop, val])(policyContactDetails);
    };

    const policyAdditionalInterest = parentKey => ([prop, val]) => (policyContactDetails) => {
        return _.filter(policyContactDetails, {
            policyContactRoles: [{
                subtype: 'PolicyAddlInterest',
                policyAdditionalInterest: {
                    [parentKey]: [{ [prop]: val }]
                }
            }]
        }) || {};
    };

    const policyStatus = useCallback(() => {
        if (!isCommercial) {
            return _.get(currentPeriodDetails, 'lobs.homeOwners.status')
        } else if (isCommercial) {
            return _.get(currentPeriodDetails, 'lobs.commercialProperty.status')
        }

        return undefined;
    })

    const policyTypeDisplayName = useCallback(() => {
        if (!isCommercial) {
            return _.get(currentPeriodDetails, 'lobs.homeOwners.dwellingDTO.policyType')
        } else if (isCommercial) {
            return _.get(currentPeriodDetails, 'lobs.commercialProperty.coverageFormTypeDisplayName_TWIA')
        }

        return undefined;
    })

    const getAdditionalInterestContactAndDetail = (property, policyContactDetails = {}) => {
        const [policyContactDetail] = policyContactDetails;
        let aid = {};

        if (!_.isEmpty(policyContactDetail)) {
            ({
                policyContactRoles: [{
                    policyAdditionalInterest: {
                        [property]: [aid = []]
                    } = {}
                }] = []
            } = policyContactDetail);
        }

        return {
            additionalInterestContact: policyContactDetail,
            additionalInterestDetail: aid
        };
    };

    const totalPremiumInclTaxesandFees = () => {
        const totalPremiumAmount = _.get(currentPeriodDetails, 'premium.amount');
        const taxesAndFeesAmount = _.get(currentPeriodDetails, 'charges.amount');

        const totalAmount = taxesAndFeesAmount + totalPremiumAmount;

        return {
            currency: 'usd',
            amount: totalAmount
        };

    };

    const overrideProps = {
        policyStatusValue: {
            value: policyStatus(),
            label: isOffer ? translator(messages.status) : translator(messages.policyStatus)
        },
        primaryInsured: {
            value: primaryNamedInsuredDisplayName()
        },
        policyType: {
            value: policyTypeDisplayName(),
            label: isCommercial ? translator(messages.CoverageForm) : translator(messages.PolicyType)
        },
        mortgagee: {
            value: mortgageeDisplayName()
        },
        mortgageeColumn: {
            visible: !isCommercial && !_.isUndefined(mortgageeName.current)
        },
        comercialColumn: {
            visible: isCommercial || !isCommercial && _.isUndefined(mortgageeName.current)
        },
        policyStartValue: {
            label: isOffer ? translator(messages.RequestedEffectiveDate) : translator(messages.PolicyStart)
        },
        policyExpirationValue: {
            label: isOffer ? translator(messages.RequestedExpirationDate) : translator(messages.PolicyExpiration)
        },
        totalPremiumValue: {
            value: totalPremiumInclTaxesandFees(),
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            model={currentPeriodDetails}
            classNameMap={styles}
        />
    );
}

DetailsAccordion.propTypes = {
    value: PropTypes.shape({}),
};

DetailsAccordion.defaultProps = {
    value: {},
};

export default DetailsAccordion;
